import { useEffect, useState } from 'react'
import { Menu, Bell, ChevronDown, User, Settings, LogOut } from 'lucide-react'
import { getBusinessProfile } from '../../services/dashboardApi'

import { useLang } from '../../core/lang'
import LangKeys from '../../core/locales/keys'

const titleTranslationKeys = {
  'Dashboard': LangKeys.home,
  'Parties': LangKeys.my_khata,
  'Cashbook': LangKeys.cashbook,
  'Bills': LangKeys.bills,
  'Items': LangKeys.items,
  'Reports': LangKeys.report,
  'Reminders': LangKeys.reminder,
  'Business Profile': LangKeys.business_settings,
  'Help & Support': LangKeys.help_support_title,
  'Settings': LangKeys.settings,
};

function Header({
  title = 'Dashboard',
  isMobile = false,
  onMenuClick,
  setActiveTab,
  showDropdown,
  setShowDropdown,
  profile,
}) {

  const { t } = useLang()

  // =====================================================
  // PREMIUM AVATAR INITIAL
  // =====================================================

  const getInitial = () => {

    if (
      profile?.business_name
    ) {
      return profile.business_name
        .trim()[0]
        .toUpperCase()
    }

    if (
      profile?.owner_name
    ) {
      return profile.owner_name
        .trim()[0]
        .toUpperCase()
    }

    return 'M'
  }

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="no-print" style={styles.header}>

      {/* LEFT */}
      <div style={styles.left}>

        {isMobile && (
          <button
            style={styles.menuBtn}
            onClick={onMenuClick}
          >
            <Menu size={20} />
          </button>
        )}

        <div>
          <h1 style={styles.title}>
            {t(titleTranslationKeys[title]) || title}
          </h1>

          <p style={styles.subtitle}>
            {t(LangKeys.header_subtitle)}
          </p>
        </div>
      </div>

      {/* RIGHT */}
      <div style={styles.right}>

        {/* TIME & DATE SECTION (PREMIUM) */}
        {!isMobile && (
          <div style={styles.dateTimeContainer}>
            <div style={styles.timeWrapper}>
              <span style={styles.clockIcon}>🕒</span>
              <span style={styles.timeText}>{formatTime(currentTime)}</span>
            </div>
            <div style={styles.dateBadge}>
              {formatDate(currentTime)}
            </div>
          </div>
        )}

        {/* NOTIFICATION */}
        <button style={styles.iconBtn}>
          <Bell
            size={18}
            strokeWidth={2.2}
          />
        </button>

        {/* PROFILE SECTION */}
        <div style={styles.profileWrapper}>
          <div
            style={styles.profile}
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <div style={styles.avatarWrapper}>
              {profile?.logo_url ? (
                <img
                  src={profile.logo_url}
                  alt="Business Logo"
                  style={styles.avatar}
                />
              ) : (
                <div style={styles.initialAvatar}>
                  {getInitial()}
                </div>
              )}
            </div>

            <div style={styles.profileText}>
              <div style={styles.name}>
                {profile?.business_name || t(LangKeys.moxpal_khata)}
              </div>
              <div style={styles.role}>
                {profile?.owner_name || t(LangKeys.business_owner)}
              </div>
            </div>

            <ChevronDown
              size={14}
              style={{
                marginLeft: '4px',
                color: '#64748B',
                transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease'
              }}
            />
          </div>
        </div>
      </div>


    </div>
  )
}

const styles = {

  // =====================================================
  // HEADER
  // =====================================================

  header: {
    minHeight: '64px',
    background: 'rgba(255,255,255,0.88)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderBottom: '1px solid rgba(226,232,240,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 22px',

    gap: '14px',

    flexWrap: 'wrap',

    position: 'sticky',

    top: 0,

    zIndex: 50,
  },

  // =====================================================
  // LEFT
  // =====================================================

  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  menuBtn: {
    width: '42px',
    height: '42px',

    border:
      '1px solid #E2E8F0',

    borderRadius: '14px',

    backgroundColor:
      '#FFFFFF',

    display: 'flex',

    alignItems: 'center',

    justifyContent:
      'center',

    cursor: 'pointer',

    boxShadow:
      '0 4px 14px rgba(15,23,42,0.06)',
  },

  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#0F172A',
    margin: 0,
    lineHeight: '30px',
    letterSpacing: '-0.02em',
  },

  subtitle: {
    fontSize: '13px',
    color: '#64748B',
    marginTop: '3px',
    lineHeight: '18px',
    fontWeight: '500',
    maxWidth: '420px',
  },

  // =====================================================
  // RIGHT
  // =====================================================

  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },

  // =====================================================
  // SEARCH
  // =====================================================

  dateTimeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginRight: '12px',
  },
  timeWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#F1F5F9',
    padding: '8px 14px',
    borderRadius: '14px',
    border: '1px solid #E2E8F0',
  },
  clockIcon: {
    fontSize: '14px',
  },
  timeText: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#0F172A',
    minWidth: '100px',
    textAlign: 'center',
  },
  dateBadge: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#6366F1',
    backgroundColor: 'rgba(99, 102, 241, 0.08)',
    padding: '8px 14px',
    borderRadius: '14px',
    border: '1px solid rgba(99, 102, 241, 0.15)',
  },

  // =====================================================
  // NOTIFICATION
  // =====================================================

  iconBtn: {
    width: '44px',
    height: '44px',

    borderRadius: '14px',

    border:
      '1px solid #E2E8F0',

    backgroundColor:
      '#FFFFFF',

    display: 'flex',

    alignItems: 'center',

    justifyContent:
      'center',

    cursor: 'pointer',

    color: '#334155',

    boxShadow:
      '0 4px 14px rgba(15,23,42,0.06)',
  },

  // =====================================================
  // PROFILE SECTION
  // =====================================================

  profileWrapper: {
    position: 'relative',
  },

  profile: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 12px 6px 8px',
    borderRadius: '30px',
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: '#F8FAFC',
      borderColor: '#CBD5E1',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
    }
  },

  avatarWrapper: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    overflow: 'hidden',
    border: '1px solid #E2E8F0',
  },

  avatar: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  initialAvatar: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #6366F1, #4338CA)',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
  },

  profileText: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },

  name: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#1E293B',
    lineHeight: '1.2',
  },

  role: {
    fontSize: '11px',
    color: '#64748B',
    lineHeight: '1.2',
    marginTop: '1px',
  },

  // =====================================================
  // DROPDOWN POPUP
  // =====================================================

  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998, // Just below the mobile dropdown
  },

  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 12px)',
    right: 0,
    width: '260px',
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    boxShadow: '0 20px 40px rgba(15, 23, 42, 0.12)',
    padding: '8px',
    zIndex: 101,
    animation: 'dropdownFade 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'column',
  },

  mobileDropdown: {
    position: 'fixed',
    bottom: '0',
    left: '0',
    right: '0',
    width: '100%',
    maxWidth: 'none',
    backgroundColor: '#FFFFFF',
    background: '#FFFFFF', // Fallback
    borderRadius: '32px 32px 0 0',
    boxShadow: '0 -10px 50px rgba(0, 0, 0, 0.3)',
    padding: '12px 20px 40px 20px',
    zIndex: 9999, // Extremely high z-index
    animation: 'mobileDropdownFade 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    display: 'flex',
    flexDirection: 'column',
    opacity: 1,
    visibility: 'visible',
    isolation: 'isolate',
  },

  mobileHandle: {
    width: '40px',
    height: '4px',
    backgroundColor: '#E2E8F0',
    borderRadius: '2px',
    margin: '0 auto 12px auto',
  },

  dropdownHeader: {
    padding: '12px 12px 16px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  largeAvatarWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    overflow: 'hidden',
    border: '2px solid #F1F5F9',
  },

  largeAvatar: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  largeInitialAvatar: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #6366F1, #4338CA)',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '600',
  },

  headerInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },

  headerName: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#0F172A',
  },

  headerEmail: {
    fontSize: '12px',
    color: '#64748B',
    fontWeight: '500',
  },

  divider: {
    height: '1px',
    backgroundColor: '#F1F5F9',
    margin: '4px 0',
  },

  menuList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    margin: '4px 0',
  },

  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
    color: '#475569',
  },

  menuText: {
    fontSize: '14px',
    fontWeight: '500',
    flex: 1,
  },

  menuIconBox: {
    width: '32px',
    height: '32px',
    borderRadius: '10px',
    backgroundColor: '#F8FAFC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748B',
    transition: 'all 0.2s ease',
  },
}

export default Header